import "@/components/FullPageEditor/styles/richEditor.css";
import "katex/dist/katex.min.css";

import { Flex, FlexColumn } from "@/components/Flex";
import { useEditorContextSelector } from "@/components/FullPageEditor/hooks/useEditorContextSelector";
import { TableOfContentsUI } from "@/components/FullPageEditor/components/TableOfContentsUI";
import TextToolbar from "@/components/FullPageEditor/components/menus/TextMenu/TextToolbar";
import { EDITOR_CSS_VAR } from "@/components/FullPageEditor/utils/constants";
import HtmlText from "@/components/HtmlText";
import LoadingAnimation from "@/components/LoadingAnimation";
import { useNotePageContextSelector } from "@/components/NotePage/hooks/NotePageContext";
import { useMounted } from "@/hooks/useMounted";
import br from "@/styles/breakpoints.module.css";
import { getHtmlFromContent } from "@knowt/editor/helpers/getHtmlFromContent";
import { EditorContent } from "@knowt/editor/react";
import { ItemType } from "@knowt/syncing/graphql/schema";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import clsx from "clsx";
import { useSidebarContextSelector } from "../../hooks/SidebarContext";
import Title from "./NoteMainTitle";
import NoteTitleEmojiPicker from "./NoteMainTitle/NoteTitleEmojiPicker";
import styles from "./editor.module.css";
import dynamic from "next/dynamic";

const EditorMenus = dynamic(() => import("@/components/FullPageEditor/components/menus/EditorMenus"), { ssr: false });

const Editor = ({
    hideTitle,
    editorStyle,
    editorClassName,
    toolbarStyle,
}: {
    hideTitle?: boolean;
    hideChatButton?: boolean;
    editorStyle?: React.CSSProperties;
    editorClassName?: string;
    toolbarStyle?: React.CSSProperties;
}) => {
    const { user } = useCurrentUser();

    const isNoteOwner = useNotePageContextSelector(state => state.isNoteOwner);
    const note = useNotePageContextSelector(state => state.note);

    const editor = useEditorContextSelector(context => context.editor);
    const nonEditable = useNotePageContextSelector(state => state.nonEditable);
    const printDocument = useNotePageContextSelector(state => state.printDocument);
    const isNoteChatSplitOpen = useSidebarContextSelector(state => state.isNoteChatSplitOpen);

    const mounted = useMounted();

    return (
        <FlexColumn
            className={clsx(editorClassName)}
            style={{ width: "100%", margin: "0 auto", maxWidth: "80rem", position: "relative", ...editorStyle }}>
            {isNoteOwner && (
                <TextToolbar
                    editor={editor}
                    isLoading={!editor}
                    style={{
                        flex: 1,
                        marginBottom: "2.4rem",
                        position: "sticky",
                        top: "8rem",
                        ...toolbarStyle,
                    }}
                    onExportPDF={() => printDocument()}
                />
            )}
            {/* TITLE */}
            <div
                style={{ padding: `0 ${EDITOR_CSS_VAR.SIDE_PADDING}`, display: !hideTitle ? "block" : "none" }}
                data-testid="auto-id-0aebbfbc-ac1e-41d0-a77a-1d6d952680ef">
                <NoteTitleEmojiPicker
                    sx={{
                        transitionDelay: "800ms",
                        opacity: note?.icon?.emoji ? 1 : 0,
                        width: note?.icon?.emoji ? "10rem" : "fit-content",
                        marginLeft: note?.icon?.emoji ? "-2rem" : "0",
                        "&:hover": { opacity: 1, transitionDelay: "1ms" },
                    }}
                />
                <Title
                    key={note?.noteId}
                    onGoToNextInput={() => editor?.commands.focus("start")}
                    style={{ marginBottom: "2.4rem" }}
                    data-testid="auto-id-ef9ebbd4-ac31-40c5-84c4-627d6c0a831f" />
            </div>
            <Flex className={br.mdDownDisplayNone} style={{ justifyContent: "flex-end" }}>
                {editor && (
                    <TableOfContentsUI
                        editor={editor}
                        style={{
                            position: "fixed",
                            zIndex: 10,
                            top: "50%",
                            translate: "0 -50%",
                            right: isNoteChatSplitOpen ? undefined : "2rem",
                        }}
                    />
                )}
            </Flex>
            {(() => {
                if (mounted) {
                    return <EditorContent className={styles.container} editor={editor} />;
                }

                // preview for owner before its mounted
                if (isNoteOwner) {
                    return <LoadingAnimation />;
                }

                // preview content for viewer is the content - this helps SEO since the content exists before it's mounted
                return (
                    <HtmlText
                        text={getHtmlFromContent({ content: note?.content ?? "", type: "note" })}
                        style={{
                            backgroundColor: "transparent",
                            fontFamily: "sans-serif",
                            maxWidth: "100%",
                            padding: `0 ${EDITOR_CSS_VAR.SIDE_PADDING}`,
                        }}
                        className={styles.container}
                    />
                );
            })()}
            {editor && !nonEditable && (
                <EditorMenus
                    GhostTextMenuProps={{ itemId: note?.noteId, itemType: ItemType.NOTE }}
                    editor={editor}
                    disabled={(() => {
                        if (note?.autocompleteSettings?.btnDisabled || user?.autocompleteSettings?.btnDisabled) {
                            return ["GhostTextMenu"];
                        }

                        return [];
                    })()}
                />
            )}
        </FlexColumn>
    );
};

export default Editor;
